import React, { useState } from 'react'
import { useHistory, Link } from 'react-router-dom'

import useQuery from '@/hooks/useQuery'
import { useGlobalState } from '@/state'
import { randomToastSuccess } from '@/shared/Toast'
import Card from '@/shared/Card'
import PrimaryButton from '@/shared/Buttons/Primary'

import FeedbackImageUploader from './FeedbackImageUploader'
import { CATEGORY_LABELS } from './shared'

const FeedbackNew = () => {
  const history = useHistory()
  const { putpostRequest } = useQuery()
  const [, setToast] = useGlobalState('toast')
  const [currentUser] = useGlobalState('currentUser')

  const [category, setCategory] = useState('feature_request')
  const [title, setTitle] = useState('')
  const [description, setDescription] = useState('')
  const [imageUrls, setImageUrls] = useState([])
  const [submitting, setSubmitting] = useState(false)
  const [errors, setErrors] = useState({})

  if (!currentUser?.id) {
    return (
      <div className='py-12 text-center'>
        <p className='text-gray-600 dark:text-gray-300'>
          Please <a href={`/login?fwd=${encodeURIComponent('/feedback/new')}`} className='text-clcpurple font-semibold'>log in</a> to submit feedback.
        </p>
      </div>
    )
  }

  const submit = () => {
    setErrors({})
    if (!title.trim()) {
      setErrors({ title: 'Required' })
      return
    }
    setSubmitting(true)
    putpostRequest('/api/v1/feedbacks', 'POST', {
      feedback: { title: title.trim(), description: description.trim(), category },
      image_urls: imageUrls
    }, (err, json) => {
      setSubmitting(false)
      if (err) {
        if (typeof err === 'object') setErrors(err)
        return
      }
      setToast(randomToastSuccess())
      history.push(`/feedback/${json.feedback.slug}`)
    })
  }

  return (
    <div className='py-8 max-w-3xl mx-auto'>
      <Link to='/feedback' className='text-sm text-clcpurple hover:underline'>← Back to feedback</Link>
      <h1 className='mt-3 text-2xl sm:text-3xl font-bold text-gray-900 dark:text-gray-100'>New feedback</h1>

      <Card className='mt-5' footer={<PrimaryButton onClick={submit} loading={submitting} text='Submit' />}>
        <div className='space-y-5'>
          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Type</label>
            <div className='flex gap-2'>
              {Object.entries(CATEGORY_LABELS).map(([value, label]) => (
                <button
                  key={value}
                  type='button'
                  onClick={() => setCategory(value)}
                  className={`px-3 py-2 rounded-md text-sm font-semibold border ${
                    category === value
                      ? 'bg-clcpurple text-white border-clcpurple'
                      : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-200 border-gray-300 dark:border-gray-600 hover:border-clcpurple'
                  }`}
                >
                  {label}
                </button>
              ))}
            </div>
          </div>

          <div>
            <label htmlFor='title' className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Title</label>
            <input
              id='title'
              type='text'
              value={title}
              maxLength={200}
              onChange={(e) => setTitle(e.target.value)}
              placeholder='In one sentence, what would you like?'
              className={errors.title ? 'errors' : ''}
            />
            <div className='mt-1 flex justify-between text-xs text-gray-500'>
              <span>{errors.title && <span className='text-red-600'>{errors.title}</span>}</span>
              <span>{title.length}/200</span>
            </div>
          </div>

          <div>
            <label htmlFor='description' className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Details</label>
            <textarea
              id='description'
              rows={6}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder='Add context, screenshots below if useful, and what you expect to happen.'
              className={errors.description ? 'errors' : ''}
            />
          </div>

          <div>
            <label className='block text-sm font-medium text-gray-700 dark:text-gray-200 mb-1'>Screenshots (optional)</label>
            <FeedbackImageUploader urls={imageUrls} onChange={setImageUrls} max={5} />
          </div>
        </div>
      </Card>
    </div>
  )
}

export default FeedbackNew
